import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { PartyPopper } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SectionHeading } from "./SectionHeading";

const EMPTY = { name: "", phone: "", email: "", event_date: "", inflatable_id: "", address: "", zip: "", notes: "" };

const inputCls =
    "rounded-xl border-2 border-monster-navy bg-white px-4 py-3 font-semibold text-monster-navy shadow-sticker-sm focus-visible:ring-monster-blue placeholder:text-monster-navy/35 placeholder:font-normal";

export const BookingForm = ({ selected, onSelect }) => {
    const [form, setForm] = useState(EMPTY);
    const [inflatables, setInflatables] = useState([]);
    const [bookedDates, setBookedDates] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [confirmed, setConfirmed] = useState(null);
    const [delivery, setDelivery] = useState(null);
    const [checkingDelivery, setCheckingDelivery] = useState(false);

    const checkDelivery = async () => {
        if (form.zip.trim().length !== 5) return;
        setCheckingDelivery(true);
        setDelivery(null);
        try {
            const res = await api.post("/delivery-check", { zip: form.zip.trim() });
            setDelivery(res.data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setCheckingDelivery(false);
        }
    };

    useEffect(() => {
        api.get("/inflatables").then((res) => setInflatables(res.data)).catch(() => {});
    }, []);

    useEffect(() => {
        if (selected) setForm((f) => ({ ...f, inflatable_id: selected }));
    }, [selected]);

    useEffect(() => {
        if (!form.inflatable_id) {
            setBookedDates([]);
            return;
        }
        api.get(`/availability/${form.inflatable_id}`)
            .then((res) => setBookedDates(res.data.booked_dates || []))
            .catch(() => setBookedDates([]));
    }, [form.inflatable_id]);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
    const bookedDateObjs = bookedDates.map((d) => new Date(`${d}T12:00:00`));
    const dateObj = form.event_date ? new Date(`${form.event_date}T12:00:00`) : undefined;
    const disabledDays = [{ before: new Date() }, ...bookedDateObjs];
    const handleDateSelect = (d) => setForm((f) => ({ ...f, event_date: d ? format(d, "yyyy-MM-dd") : "" }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.event_date) {
            toast.error("Pick your party date on the calendar first.");
            return;
        }
        setSubmitting(true);
        try {
            const res = await api.post("/bookings", { ...form, email: form.email || null });
            setConfirmed(res.data);
            setForm(EMPTY);
            onSelect?.("");
            toast.success("Booking request sent! We'll call you to confirm.");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section id="book" data-testid="booking-section" className="border-t-2 border-monster-navy bg-monster-green/15 py-24 sm:py-32">
            <div className="mx-auto max-w-4xl px-5">
                <SectionHeading
                    number="04"
                    title="Book your"
                    highlight="monster."
                    sub="Tell us when and where — we'll call or text you back within a few hours to confirm your date and details."
                />
                <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ type: "spring", stiffness: 80, damping: 15 }}
                    className="rounded-[32px] border-2 border-monster-navy bg-white p-7 shadow-sticker-lg sm:p-10"
                >
                    {confirmed ? (
                        <div data-testid="booking-success" className="py-8 text-center">
                            <span className="mx-auto flex h-20 w-20 items-center justify-center rounded-full border-2 border-monster-navy bg-monster-green shadow-sticker">
                                <PartyPopper className="h-9 w-9 text-monster-navy" />
                            </span>
                            <h3 className="mt-6 font-display text-3xl font-semibold text-monster-navy">You're on the books, {confirmed.name.split(" ")[0]}!</h3>
                            <p className="mx-auto mt-3 max-w-md text-sm font-semibold text-monster-navy/60">
                                We received your request for the <span className="text-monster-blue">{confirmed.inflatable_name}</span> on{" "}
                                <span className="text-monster-blue">{confirmed.event_date}</span>. We'll reach out at {confirmed.phone} shortly to confirm.
                            </p>
                            <button
                                data-testid="booking-another-btn"
                                onClick={() => setConfirmed(null)}
                                className="mt-8 rounded-full border-2 border-monster-navy bg-monster-yellow px-7 py-3 font-display text-sm font-semibold text-monster-navy shadow-sticker-sm transition-transform hover:-translate-y-0.5"
                            >
                                Book Another Monster
                            </button>
                        </div>
                    ) : (
                        <form data-testid="booking-form" onSubmit={submit} className="grid gap-6 sm:grid-cols-2">
                            <p data-testid="booking-launch-note" className="rounded-2xl border-2 border-dashed border-monster-blue/40 bg-monster-sky px-4 py-3 text-sm font-semibold text-monster-navy/70 sm:col-span-2">
                                We open Spring 2027 — booking requests are for party dates after launch day.
                            </p>
                            <div>
                                <Label htmlFor="bk-name" className="mb-2 block font-display text-sm font-semibold text-monster-navy">Your name *</Label>
                                <Input id="bk-name" data-testid="booking-name-input" required value={form.name} onChange={set("name")} placeholder="Jane Monster" className={inputCls} />
                            </div>
                            <div>
                                <Label htmlFor="bk-phone" className="mb-2 block font-display text-sm font-semibold text-monster-navy">Phone *</Label>
                                <Input id="bk-phone" data-testid="booking-phone-input" required type="tel" value={form.phone} onChange={set("phone")} placeholder="(810) 555-1234" className={inputCls} />
                            </div>
                            <div>
                                <Label htmlFor="bk-email" className="mb-2 block font-display text-sm font-semibold text-monster-navy">Email</Label>
                                <Input id="bk-email" data-testid="booking-email-input" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputCls} />
                            </div>
                            <div className="sm:col-span-2">
                                <Label className="mb-2 block font-display text-sm font-semibold text-monster-navy">Party date *</Label>
                                {!form.inflatable_id ? (
                                    <p data-testid="booking-date-hint" className="rounded-xl border-2 border-dashed border-monster-navy/30 bg-monster-cream px-4 py-3 text-sm font-semibold text-monster-navy/50">
                                        Pick your monster below first — then we'll show you which dates are already booked.
                                    </p>
                                ) : (
                                    <div className="flex flex-col items-center gap-5 rounded-2xl border-2 border-monster-navy bg-monster-cream p-4 shadow-sticker-sm sm:flex-row sm:items-start sm:justify-center sm:p-5">
                                        <div className="overflow-hidden rounded-xl border-2 border-monster-navy bg-white" data-testid="booking-date-calendar">
                                            <Calendar
                                                mode="single"
                                                selected={dateObj}
                                                onSelect={handleDateSelect}
                                                disabled={disabledDays}
                                                modifiers={{ booked: bookedDateObjs }}
                                                modifiersClassNames={{ booked: "line-through decoration-monster-red decoration-2 text-monster-red" }}
                                            />
                                        </div>
                                        <div className="text-sm font-semibold text-monster-navy/70 sm:pt-3">
                                            <p data-testid="booking-date-selected" className="font-display text-base font-semibold text-monster-navy">
                                                {form.event_date ? `Party date: ${form.event_date}` : "Tap an open date"}
                                            </p>
                                            <p className="mt-3 flex items-center gap-2">
                                                <span className="inline-block h-3.5 w-3.5 rounded-full border border-monster-navy bg-monster-green" /> Open date
                                            </p>
                                            <p className="mt-1.5 flex items-center gap-2">
                                                <span className="inline-block h-3.5 w-3.5 rounded-full border border-monster-navy bg-monster-red" /> Already booked
                                            </p>
                                            {bookedDates.length > 0 && (
                                                <p className="mt-3 max-w-[200px] text-xs text-monster-navy/50">
                                                    {bookedDates.length} date{bookedDates.length === 1 ? "" : "s"} already taken for this monster — crossed out in red.
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="sm:col-span-2">
                                <Label className="mb-2 block font-display text-sm font-semibold text-monster-navy">Pick your monster *</Label>
                                <Select required value={form.inflatable_id} onValueChange={(v) => { setForm((f) => ({ ...f, inflatable_id: v })); onSelect?.(v); }}>
                                    <SelectTrigger data-testid="booking-inflatable-select" className={inputCls}>
                                        <SelectValue placeholder="Choose an inflatable" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {inflatables.map((i) => (
                                            <SelectItem key={i.id} value={i.id} data-testid={`booking-inflatable-option-${i.id}`}>
                                                {i.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label htmlFor="bk-address" className="mb-2 block font-display text-sm font-semibold text-monster-navy">Party address *</Label>
                                <Input id="bk-address" data-testid="booking-address-input" required value={form.address} onChange={set("address")} placeholder="123 Party Lane, Grand Blanc, MI" className={inputCls} />
                            </div>
                            <div>
                                <Label htmlFor="bk-zip" className="mb-2 block font-display text-sm font-semibold text-monster-navy">Delivery zip code *</Label>
                                <div className="flex gap-2">
                                    <Input id="bk-zip" data-testid="booking-zip-input" required inputMode="numeric" maxLength={5} value={form.zip} onChange={set("zip")} placeholder="48430" className={inputCls} />
                                    <button
                                        type="button"
                                        data-testid="delivery-check-btn"
                                        onClick={checkDelivery}
                                        disabled={checkingDelivery || form.zip.trim().length !== 5}
                                        className="shrink-0 rounded-xl border-2 border-monster-navy bg-monster-yellow px-4 font-display text-sm font-semibold text-monster-navy shadow-sticker-sm transition-all hover:-translate-y-0.5 disabled:opacity-50"
                                    >
                                        {checkingDelivery ? "..." : "Check"}
                                    </button>
                                </div>
                            </div>
                            {delivery && (
                                <div
                                    data-testid="delivery-result"
                                    className={`rounded-2xl border-2 border-monster-navy px-4 py-3 text-sm font-bold shadow-sticker-sm sm:col-span-2 ${
                                        !delivery.in_area ? "bg-monster-red/15" : delivery.fee > 0 ? "bg-monster-yellow" : "bg-monster-green/25"
                                    }`}
                                >
                                    {delivery.message}
                                    {delivery.fee > 0 && " Added to your quote — pay on delivery day."}
                                </div>
                            )}
                            <div className="sm:col-span-2">
                                <Label htmlFor="bk-notes" className="mb-2 block font-display text-sm font-semibold text-monster-navy">Anything we should know?</Label>
                                <Textarea id="bk-notes" data-testid="booking-notes-input" rows={3} value={form.notes} onChange={set("notes")} placeholder="Grass or pavement? Gate access? 20 sugar-fueled kids?" className={inputCls} />
                            </div>
                            <div className="sm:col-span-2">
                                <button
                                    data-testid="booking-submit-btn"
                                    type="submit"
                                    disabled={submitting}
                                    className="w-full rounded-full border-2 border-monster-navy bg-monster-blue py-4 font-display text-lg font-semibold text-white shadow-sticker transition-all hover:-translate-y-0.5 hover:bg-[#1a63c2] active:translate-y-1 active:shadow-none disabled:opacity-60"
                                >
                                    {submitting ? "Sending..." : "Request My Booking"}
                                </button>
                                <p className="mt-3 text-center text-xs font-semibold text-monster-navy/45">No payment due now — we'll work out the details together once we confirm your date.</p>
                            </div>
                        </form>
                    )}
                </motion.div>
            </div>
        </section>
    );
};
